import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { NewsItemModel } from '@app/reducers/orm/news-item/news-item.model';
import { DashboardFilterType } from '@app/reducers/page-filters/page-filters-dashboard.config';
import { selectAllowedDashboardFilter } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { getNewsItemsForAuthenticatedUser, NewsItemService } from '@reducers/orm/news-item/news-item.service';
import { PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { mgcIconNews2CuteRe } from '@sb/mgc-icons/mgcIcon-news_2_cute_re.icon';
import { DashboardWidgetModule } from '@sb/ui/dashboard-widget/dashboard-widget.module';
import { IconRegistry } from '@sb/ui/icon';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { subWeeks } from 'date-fns';
import { finalize } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'my-news-items-widget',
  templateUrl: './my-news-items-widget.component.html',
  imports: [DashboardWidgetModule, TranslatePipe, LocaleDatePipe, RouterLink],
})
export class MyNewsItemsWidgetComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private newsItemService = inject(NewsItemService);
  private pageFilterService = inject(PageFiltersService);
  private iconRegistry = inject(IconRegistry);
  private translate = inject(TranslateService);
  private router = inject(Router);

  periodOptions = [
    { label: this.translate.instant('week'), value: 1 },
    { label: this.translate.instant('2 weeks'), value: 2 },
    { label: this.translate.instant('3 weeks'), value: 3 },
    { label: this.translate.instant('4 weeks'), value: 4 },
  ];

  isLoading = signal(false);
  newsItems = computed<NewsItemModel[]>(() =>
    this.store.selectSignal(
      getNewsItemsForAuthenticatedUser(
        toISODateString(subWeeks(new Date(), this.widgetPeriod())),
        toISODateString(new Date()),
      ),
    )(),
  );
  private filterType = DashboardFilterType.MY_NEWS_ITEMS;
  private dashboardFilter = this.store.selectSignal(selectAllowedDashboardFilter(this.filterType));
  widgetPeriod = computed(() => this.dashboardFilter()?.widgetPeriod ?? 1);

  constructor() {
    this.iconRegistry.registerIcons([mgcIconNews2CuteRe]);
  }

  ngOnInit() {
    this.isLoading.set(true);
    void this.newsItemService
      .load({ include: 'User,Attachment,Department', limit: 999 })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  onPeriodChange(period: number | string) {
    this.pageFilterService.setFilter('dashboard', this.filterType, {
      widgetPeriod: period,
    });
  }

  onAddNewsMessageClick() {
    this.router.navigate(['', { outlets: { modal: ['news-item'] } }]);
  }
}
